import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { processMathEquation } from '../components/utils/latexParser';
import { PAPERS_V4, getSheetGeometry } from './printSpec';
import { distributeProblemsV4, distributeSolutionsV4, measureProblemHeightsMm } from './measureV4';
import PrintSheetV4 from './PrintSheetV4';
import { printV4, PV4_PORTAL_ID } from './usePrintV4';
import { PRESETS, presetKeysFor, makePatternOf, isAllAuto, patternOfPage } from './pagePatterns';
import PagePatternFloat from './PagePatternFloat';
import { PRINT_DEFAULTS, loadPrintPrefs, savePrintPrefs } from './printPrefs';
import { B4_MARKER_LABEL_MAX_LENGTH, b4MarkersForSheet, isB4MarkerNumberTaken } from './b4Markers';
import { useAuth } from '../context/AuthContext';
import { B4_HEADER_FIELDS, normalizePrintContext, printDraftScope } from './printDrafts';
import useB4PrintDraft from './useB4PrintDraft';
import { PRINT_ANSWER_BATCH, chunkIds } from './printRequestLimits';

/**
 * PrintOverlayV4 — 실측 규격(A4/B4) 신 인쇄 오버레이 (v2 병행용, FE 신 인쇄 경로).
 *
 * 기존 PrintPreview(v2)와 완전 분리된 병행 경로: 입력은 v2와 동일한 Lexical→HTML 변환
 * 결과(problems[{problem_id, content, answer?}], solutions[{problem_id, number, content}])를
 * 받고, 용지(A4/B4)를 화면에서 분기해 실치수로 재배치·인쇄한다.
 * 정답은 v2와 동일 계약: preset이면 problems에 내장된 answer, 아니면 /api/solutions/answers.
 * 인쇄/PDF 저장은 브라우저 인쇄 대화상자 하나로 통일(서버 PDF 불필요).
 */

// 시험지 편집 모달의 인라인 지면(ExamPapersV2/PaperComposeModal 우측 칸)도 이 훅을 그대로 쓴다 —
// 정답 왕복(preset 내장 판정 → /api/solutions/answers → KaTeX 조판)의 사본을 늘리지 않으려고
// export 한다. 훅이라 동적 import 로는 부를 수 없다.
export function useAnswers(problems, open) {
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || problems.length === 0) {
      setAnswers([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);

    const renderAnswer = (raw) => {
      if (!raw) return '-';
      try {
        const tempDiv = document.createElement('div');
        katex.render(processMathEquation(raw), tempDiv, { throwOnError: false, displayMode: true, strict: false });
        return tempDiv.innerHTML;
      } catch (e) {
        return processMathEquation(raw);
      }
    };

    const run = async () => {
      // preset 경로: 문제에 정답 내장 (v2와 동일 판정)
      if (problems.some((p) => p.answer !== undefined)) {
        if (!alive) return;
        setAnswers(problems.map((p, i) => ({ number: i + 1, problem_id: p.problem_id, answer: renderAnswer(p.answer) })));
        setLoading(false);
        return;
      }
      try {
        const token = localStorage.getItem('accessToken');
        const rows = [];
        // 서버 제한만큼 나눠 차례로 묻는다. 그 이하면 종전과 같은 요청 한 번이다
        for (const ids of chunkIds(problems.map((p) => p.problem_id), PRINT_ANSWER_BATCH)) {
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/solutions/answers?problem_ids=${ids.join(',')}`, {
            headers: { Authorization: token },
          });
          const data = await res.json();
          if (!alive) return;
          // 한 묶음이라도 실패하면 종전처럼 정답을 비운다 — 일부 번호만 「-」로 찍힌 정답지를 만들지 않는다
          if (data.status !== 'success') {
            setAnswers([]);
            return;
          }
          rows.push(...data.data);
        }
        setAnswers(problems.map((p, i) => {
          const found = rows.find((a) => a.problem_id === p.problem_id);
          return { number: i + 1, problem_id: p.problem_id, answer: renderAnswer(found?.answer) };
        }));
      } catch (e) {
        if (alive) setAnswers([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => { alive = false; };
  }, [problems, open]);

  return { answers, loading };
}

// 4초 상한 뒤에도 안 온 그림을 기다리는 최대 시간(trackLate 전용). 끝내 load·error 가 오지 않는 그림
// (멈춘 연결 등)이 숨은 판을 문서에 영영 남기지 않게 한다 — 넘기면 판을 치우고 그 그림 없이 지금처럼 간다.
export const LATE_IMAGE_CAP_MS = 60000;

// 콘텐츠 내 이미지를 미리 로드 — 미로드 이미지는 높이 0으로 측정되어 배치가 어긋난다.
// decode() 대기는 lazy 이미지(오프스크린이라 로드가 시작조차 안 됨)에서 영구 pending이
// 되므로 금지 — loading=eager 강제 + load/error 이벤트 + 4초 안전망으로 대기한다.
// 〃 인라인 지면도 재기 전에 같은 대기를 한다 — 그래야 면 나눔이 인쇄와 갈리지 않는다.
//
// trackLate(인라인 지면만 켠다): 4초 상한 뒤에도 안 온 그림이 있으면 판을 문서에 남겨 두고
// { late } 를 돌려준다 — late 는 남은 그림이 다 오면(load·error) 판을 치우고 끝난다. 부르는 쪽은
// 첫 측정을 그린 뒤 late 를 기다려 한 번 더 잰다. Promise 를 그대로 돌려주지 않는 이유: async 함수가
// Promise 를 돌려주면 await 가 그것까지 기다려 버려 첫 측정이 늦은 그림 뒤로 밀린다.
// 남은 그림이 없거나 trackLate 가 아니면 지금처럼 판을 바로 치우고 null.
export async function preloadContentImages(htmlChunks, { trackLate = false } = {}) {
  const holder = document.createElement('div');
  holder.style.cssText = 'position:absolute;visibility:hidden;left:-9999px;top:0;width:800px;overflow:hidden;';
  holder.innerHTML = htmlChunks.join('');
  document.body.appendChild(holder);
  let keepHolder = false;
  try {
    const imgs = Array.from(holder.querySelectorAll('img'));
    imgs.forEach((img) => { img.loading = 'eager'; });
    const unsettled = new Set();
    const waits = imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((res) => {
      unsettled.add(img);
      const done = () => { unsettled.delete(img); res(); };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    })));
    await Promise.race([
      Promise.all(waits),
      new Promise((res) => { setTimeout(res, 4000); }), // 로드 실패·미발화 시에도 진행(best-effort, v2 동일 한계)
    ]);
    if (!trackLate || unsettled.size === 0) return null;
    keepHolder = true;
    let capTimer;
    const late = Promise.race([
      Promise.all(waits),
      new Promise((res) => { capTimer = setTimeout(res, LATE_IMAGE_CAP_MS); }),
    ]).then(() => {
      clearTimeout(capTimer);
      holder.remove();
    });
    return { late };
  } finally {
    if (!keepHolder) holder.remove();
  }
}

const EMPTY_SOLUTIONS = [];
// 측정이 예외로 끝났다는 표시(heights 자리). 길이 0 이라 배치 가드가 빈 지면을 내고, settling 은 이것을 「끝남」으로 본다
const MEASURE_FAILED = Object.freeze([]);

const B4_CAPTION = { fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 4 };
// B4 툴바 2행의 고정 행 높이(px) — 세 영역의 행 라인을 정확히 겹치게 하는 단일 기준
const B4_ROW_H = 24;

/**
 * B4 툴바 각 영역의 머리줄 — 왼쪽 제목, 오른쪽 끝 초기화.
 * 초기화는 저장값이 아니라 코드의 초기셋(PRINT_DEFAULTS)으로 되돌린다.
 */
function BlockHead({ label, sub, dirty, onReset }) {
  return (
    <div style={{ ...B4_CAPTION, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
        {sub ? <span style={{ fontWeight: 400, color: '#9ca3af' }}> {sub}</span> : null}
      </span>
      <button
        type="button"
        onClick={onReset}
        disabled={!dirty}
        title="이 영역을 초기 설정으로 되돌립니다"
        style={{
          flexShrink: 0, fontSize: 10, fontWeight: 600, lineHeight: 1.7, padding: '0 7px',
          borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', color: '#9ca3af',
          whiteSpace: 'nowrap', cursor: dirty ? 'pointer' : 'default', opacity: dirty ? 1 : 0.4,
        }}
      >
        초기화
      </button>
    </div>
  );
}

// 시험지·계정이 바뀌면 상태를 새로 만든다. 이전 창의 저장 대기는 그 창의 키로 마무리된다.
function PrintOverlayV4(props) {
  const { userId } = useAuth() || {};
  const kind = props.printContext?.kind;
  const id = props.printContext?.id;
  const printContext = useMemo(() => normalizePrintContext({ kind, id }), [kind, id]);
  if (!props.open) return null;
  return <PrintOverlayContent key={printDraftScope(userId, printContext) || 'legacy'}
    {...props} userId={userId} printContext={printContext} />;
}

function PrintOverlayContent({ open, onClose, problems = [], solutions = EMPTY_SOLUTIONS, meta, onEnsureSolutions, onBeforePrint,
  solutionsLoading = false, solutionsFailedCount = 0, onRetrySolutions,
  userId, printContext }) {
  // 마지막 설정값을 첫 렌더에 한 번만 읽는다(없거나 깨졌으면 초기셋) — printPrefs 참고
  const [prefs] = useState(loadPrintPrefs);
  const [paperKey, setPaperKey] = useState(prefs.paperKey);
  const [zoom, setZoom] = useState(prefs.zoom);
  const problemIds = useMemo(() => problems.map((problem) => problem.problem_id), [problems]);
  const { prefs: b4Prefs, update: updateB4, status: draftStatus, flush: flushDraft } = useB4PrintDraft({
    userId, context: printContext, problemIds, sharedPrefs: prefs,
  });
  const { b4Line1, b4Line2, b4Title, b4Subject, b4Cover, b4CoverPhrase,
    b4Marker5, b4MarkerShort, b4MarkerCustom,
    b4Marker5Label, b4MarkerShortLabel, b4MarkerCustomLabel } = b4Prefs;
  const close = () => { flushDraft(); onClose(); };
  // 포함 옵션(A4/B4 공통) — 기본 둘 다 미포함
  const [includeSolutions, setIncludeSolutions] = useState(false); // 풀이 페이지
  const [includeAnswers, setIncludeAnswers] = useState(false); // 빠른정답 페이지
  // 딤 클릭 닫기 판정용 — 눌린 지점이 딤이었는지 기억한다(아래 onMouseDown 참고)
  const dimPressRef = useRef(false);
  // 문항 높이는 용지·문항이 바뀔 때만 잰다. 면 배열을 바꿔도 재측정이 없다(배치는 아래 memo).
  const [heights, setHeights] = useState(null);
  // 면 배열 — 용지별로 따로 들고 있어 A4↔B4를 오가도 각자의 설정이 살아 있다.
  // A4 기본값은 'auto'라 patternOf를 넘기지 않고 기존 배치 코드 경로가 그대로 실행된다.
  const [a4Preset, setA4Preset] = useState(prefs.patPreset.A4);
  const patPreset = useMemo(() => ({ A4: a4Preset, B4: b4Prefs.patPreset.B4 }), [a4Preset, b4Prefs.patPreset.B4]);
  const setPreset = (paper, value) => {
    if (paper === 'B4') updateB4({ patPreset: { B4: value } });
    else setA4Preset(value);
  };
  const [patOverrides, setPatOverrides] = useState({ A4: {}, B4: {} });
  const [solutionPages, setSolutionPages] = useState([]);
  // 지금 solutionPages 가 어느 풀이 배열로 배치됐는지 — 풀이가 막 도착한 첫 렌더는 배치 effect 가 돌기 전이다(아래 printBlocked)
  const [laidSolutions, setLaidSolutions] = useState(EMPTY_SOLUTIONS);
  const [laying, setLaying] = useState(false);
  const [printing, setPrinting] = useState(false);

  const geo = useMemo(() => getSheetGeometry(paperKey), [paperKey]);
  const { answers, loading: answersLoading } = useAnswers(problems, open);

  // 지정한 문제 번호가 현재 문항 수를 넘으면 마커를 붙이지 않는다(선택지에도 없는 값)
  const markerNo = (v) => (v === 'none' || Number(v) > problems.length ? 'none' : v);

  const sheetMeta = useMemo(() => {
    return {
      ...meta,
      b4Line1,
      b4Line2,
      b4Title,
      b4Subject,
      b4Cover,
      b4CoverPhrase,
      b4Markers: b4MarkersForSheet({ b4Marker5, b4MarkerShort, b4MarkerCustom,
        b4Marker5Label, b4MarkerShortLabel, b4MarkerCustomLabel }, problems.length),
    };
  }, [meta, b4Line1, b4Line2, b4Title, b4Subject, b4Cover, b4CoverPhrase,
    b4Marker5, b4MarkerShort, b4MarkerCustom,
    b4Marker5Label, b4MarkerShortLabel, b4MarkerCustomLabel, problems.length]);

  // 포털은 v2 모달과 동일하게 document.body로 직접 건다.
  // 래퍼 div를 body에 만들어 그 안에 fixed 루트를 넣으면, App.css의 전역
  // `* { transform-style: preserve-3d }` 때문에 래퍼가 fixed의 컨테이닝 블록이 되어
  // 오버레이가 뷰포트 밖(body 하단)에 그려진다(실브라우저 재현 확인). fixed 루트가
  // body 직속이어야 뷰포트 기준으로 배치되므로 id(PV4_PORTAL_ID)를 루트에 직접 부여한다.

  // 풀이 토글 ON인데 데이터가 없으면 부모(모달)에 fetch 요청
  useEffect(() => {
    if (open && includeSolutions && solutions.length === 0 && onEnsureSolutions) {
      onEnsureSolutions();
    }
  }, [open, includeSolutions, solutions.length, onEnsureSolutions]);

  const activeSolutions = includeSolutions ? solutions : EMPTY_SOLUTIONS;

  // 배치 계산 — 폰트 로드 완료 후 실측(측정 결과가 폰트에 좌우되므로)
  useEffect(() => {
    if (!open || problems.length === 0) {
      setHeights(null);
      setSolutionPages([]);
      setLaidSolutions(activeSolutions);
      return;
    }
    let alive = true;
    setLaying(true);
    setHeights(null); // 옛 측정값이 새 용지·문항에 섞이지 않게
    const run = async () => {
      try {
        if (document.fonts?.ready) await document.fonts.ready;
        // 측정 전 이미지 프리로드 — 미로드 이미지의 0 높이 측정 방지
        await preloadContentImages([
          ...problems.map((p) => p.content || ''),
          ...activeSolutions.map((s) => s.content || ''),
        ]);
        if (!alive) return;
        setHeights(measureProblemHeightsMm(problems, geo));
        setSolutionPages(activeSolutions.length > 0 ? distributeSolutionsV4(activeSolutions, geo) : []);
      } catch (e) {
        // 실패해도 「배치 중」에 갇히지 않는다(async-boundary §4 — 로딩에는 반드시 끝나는 경로). 높이를 못 쟀으면 실패 표시를
        // 둬 빈 지면·인쇄 열림(옛 실패 동작)으로 끝낸다. 높이는 쟀는데 풀이 배치에서 났으면 그 높이를 그대로 둔다.
        console.error('지면 측정 실패:', e);
        if (alive) setHeights((h) => (h == null ? MEASURE_FAILED : h));
      } finally {
        if (alive) {
          setLaying(false);
          setLaidSolutions(activeSolutions); // 배치가 실패해도 잠금이 풀려야 한다 — 끝나는 경로는 여기 하나다
        }
      }
    };
    run();
    return () => { alive = false; };
  }, [open, problems, activeSolutions, geo]);

  // 지면을 아직 그리면 안 되는 동안 — 측정 중(laying)이거나, 문항은 있는데 그 문항의 높이가 아직 없을 때.
  // 창이 열린 첫 렌더는 effect 가 laying 을 켜기 전이라 laying=false·heights=null 이다. laying 만 보면 그 한 프레임에
  // 문제 면 0개짜리 지면(B4 는 표지만)이 대체 글꼴로 그려졌다가 「배치 중」으로 바뀌어 두 번 깜박였다(R8 실측 13~123ms).
  // 길이 불일치는 아래 problemPages 가드와 같은 조건이다 — 문항 배열이 바뀐 직후 한 프레임은 옛 높이라 0면 지면이 그려졌다.
  // 측정 실패(MEASURE_FAILED)는 끝난 것으로 본다 — 안 그러면 「배치 중」에 영영 갇힌다.
  // 문항이 0개면 잴 것이 없으니 지금처럼 빈 지면을 그린다.
  const settling = laying || (problems.length > 0 && heights !== MEASURE_FAILED
    && (heights == null || heights.length !== problems.length));
  // 풀이를 켰는데 아직 받는 중이면 인쇄를 막는다 — 지금 누르면 풀이 없는 인쇄물이 나간다. 끄면 기다릴 이유가 없다
  const solutionsPending = includeSolutions && solutionsLoading;
  // 풀이가 도착했는데 아직 배치 전인 틈 — 이때 인쇄하면 옛 풀이 면으로 나간다. 화면 표시(settling)는 건드리지 않고 인쇄만 막는다
  const solutionsNotLaid = laidSolutions !== activeSolutions;
  const printBlocked = printing || settling || (includeAnswers && answersLoading) || solutionsPending || solutionsNotLaid;

  // 문항 수가 바뀌면 면 인덱스의 의미가 달라진다 — 엉뚱한 면에 붙지 않게 지정을 비운다
  useEffect(() => { setPatOverrides({ A4: {}, B4: {} }); }, [problems.length]);

  const patternOf = useMemo(
    () => makePatternOf(patPreset[paperKey], patOverrides[paperKey]),
    [patPreset, patOverrides, paperKey],
  );
  const allAuto = isAllAuto(patPreset[paperKey], patOverrides[paperKey]);

  // 배치는 state가 아니라 순수 계산 — 면 배열을 바꾸면 그 프레임에 다시 계산된다(재측정 없음).
  // allAuto면 patternOf를 넘기지 않아 기존 배치 경로가 그대로 실행된다(A4 기본 상태의 회귀 방지).
  const problemPages = useMemo(() => {
    if (!heights || heights.length !== problems.length) return [];
    return distributeProblemsV4(problems, geo, allAuto ? { heights } : { heights, patternOf });
  }, [problems, geo, heights, allAuto, patternOf]);

  // 면별 패턴 요약(툴바 배지) — 표시 전용. 'auto' 면은 배치 결과에서 패턴을 역산한다.
  const pageBadges = problemPages.map((p, i) => ({
    no: i + 1,
    pattern: (p.pat && p.pat.actual !== 'auto' ? p.pat.actual : patternOfPage(p)) || '·',
    pinned: !!patOverrides[paperKey][i],
  }));

  const setPagePattern = useCallback((pageIndex, pattern) => {
    setPatOverrides((prev) => {
      const next = { ...prev[paperKey] };
      if (pattern) next[pageIndex] = pattern;
      else delete next[pageIndex];
      return { ...prev, [paperKey]: next };
    });
  }, [paperKey]);

  // 미리보기 용지 위 투명 컨트롤 — 인쇄에는 나오지 않는다(.pv4-no-print)
  const renderPageOverlay = useCallback((pageIndex) => {
    const page = problemPages[pageIndex];
    if (!page) return null;
    const meta4 = page.pat;
    const actual = meta4 && meta4.actual !== 'auto' ? meta4.actual : patternOfPage(page);
    return (
      <PagePatternFloat
        pageIndex={pageIndex}
        zoom={zoom}
        current={actual}
        want={meta4 ? meta4.want : 'auto'}
        overflowNo={meta4 ? meta4.overflowNo : null}
        pinned={!!patOverrides[paperKey][pageIndex]}
        onPick={(key) => setPagePattern(pageIndex, key)}
        onReset={() => setPagePattern(pageIndex, null)}
      />
    );
  }, [problemPages, zoom, paperKey, patOverrides, setPagePattern]);

  // 공통 설정에는 용지·배율·A4 패턴만 반영한다. 시험지 B4 값은 다른 시험지의 기본값을 바꾸지 않는다.
  // 식별값 없는 기존 직접 호출은 종전 저장 계약을 유지한다.
  const legacyB4Prefs = printContext ? null : b4Prefs;
  useEffect(() => {
    const saved = legacyB4Prefs || loadPrintPrefs();
    savePrintPrefs({ ...saved, paperKey, zoom, patPreset: { ...saved.patPreset, A4: a4Preset } });
  }, [paperKey, zoom, a4Preset, legacyB4Prefs]);

  // 영역별 초기화 — 저장값이 아니라 초기셋(PRINT_DEFAULTS)으로 되돌린다.
  // 되돌린 값도 위 효과가 다시 저장하므로 다음에 열어도 초기셋 그대로다.
  const resetHeader = () => {
    updateB4(Object.fromEntries(B4_HEADER_FIELDS.map((field) => [field, PRINT_DEFAULTS[field]])));
  };
  const resetMarkers = () => {
    updateB4({ b4Marker5: PRINT_DEFAULTS.b4Marker5, b4MarkerShort: PRINT_DEFAULTS.b4MarkerShort,
      b4MarkerCustom: PRINT_DEFAULTS.b4MarkerCustom, b4Marker5Label: PRINT_DEFAULTS.b4Marker5Label,
      b4MarkerShortLabel: PRINT_DEFAULTS.b4MarkerShortLabel, b4MarkerCustomLabel: PRINT_DEFAULTS.b4MarkerCustomLabel });
  };
  const resetLayout = () => {
    setPreset(paperKey, PRINT_DEFAULTS.patPreset[paperKey]);
    setPatOverrides((prev) => ({ ...prev, [paperKey]: {} }));
  };

  const headerDirty = b4Line1 !== PRINT_DEFAULTS.b4Line1 || b4Title !== PRINT_DEFAULTS.b4Title
    || b4Subject !== PRINT_DEFAULTS.b4Subject || b4Line2 !== PRINT_DEFAULTS.b4Line2
    || b4Cover !== PRINT_DEFAULTS.b4Cover || b4CoverPhrase !== PRINT_DEFAULTS.b4CoverPhrase;
  const markersDirty = markerNo(b4Marker5) !== markerNo(PRINT_DEFAULTS.b4Marker5)
    || markerNo(b4MarkerShort) !== markerNo(PRINT_DEFAULTS.b4MarkerShort)
    || markerNo(b4MarkerCustom) !== markerNo(PRINT_DEFAULTS.b4MarkerCustom)
    || b4Marker5Label !== PRINT_DEFAULTS.b4Marker5Label
    || b4MarkerShortLabel !== PRINT_DEFAULTS.b4MarkerShortLabel
    || b4MarkerCustomLabel !== PRINT_DEFAULTS.b4MarkerCustomLabel;
  const markerRows = [
    { key: 'five', numberKey: 'b4Marker5', label: b4Marker5Label, value: b4Marker5,
      setLabel: (value) => updateB4({ b4Marker5Label: value }), set: (value) => updateB4({ b4Marker5: value }) },
    { key: 'short', numberKey: 'b4MarkerShort', label: b4MarkerShortLabel, value: b4MarkerShort,
      setLabel: (value) => updateB4({ b4MarkerShortLabel: value }), set: (value) => updateB4({ b4MarkerShort: value }) },
    { key: 'custom', numberKey: 'b4MarkerCustom', label: b4MarkerCustomLabel, value: b4MarkerCustom,
      setLabel: (value) => updateB4({ b4MarkerCustomLabel: value }), set: (value) => updateB4({ b4MarkerCustom: value }) },
  ];
  const markerNumbers = { b4Marker5, b4MarkerShort, b4MarkerCustom };
  const layoutDirty = patPreset[paperKey] !== PRINT_DEFAULTS.patPreset[paperKey]
    || Object.keys(patOverrides[paperKey]).length > 0;

  const handlePrint = async () => {
    if (printBlocked) return; // 빠른정답·풀이 포함 시 로드 완료 전 인쇄 방지
    setPrinting(true);
    try {
      flushDraft();
      if (onBeforePrint) {
        // 인쇄 직전 훅(오답노트의 인쇄 기록 POST 등) — 실패(throw)하면 인쇄하지 않는다.
        // 사용자 알림은 훅 쪽 책임. prop 미전달 경로(시험지 등)는 종전과 완전히 동일.
        try {
          await onBeforePrint();
        } catch (e) {
          return;
        }
      }
      await printV4(geo);
    } finally {
      setPrinting(false);
    }
  };

  if (!open) return null;

  const segBtn = (active) =>
    `rounded-full px-3 py-1 text-xs font-medium transition-colors ${active ? 'bg-primary/10 font-bold text-primary' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`;
  const draftStatusText = draftStatus === 'saving' ? '임시 저장 중…'
    : draftStatus === 'saved' ? '이 브라우저에 임시 저장됨'
      : draftStatus === 'unavailable' ? '브라우저 저장 실패 · 이번 세션에서만 유지됩니다'
        : draftStatus === 'session' ? '이번 작업에서만 유지됩니다'
          : 'B4 설정은 이 브라우저에 자동 저장됩니다';

  // B4 2행(헤더·마커) 공통 — 라벨/컨트롤 2열 그리드로 좌우 라인을 맞춘다.
  // 입력은 그리드 셀(고정 폭)을 꽉 채워 세 줄의 좌·우 끝이 정확히 일치한다(border-box).
  // 세 영역의 행 라인을 완전히 일치시키기 위해 행 높이를 고정한다 — 입력과 셀렉트는
  // 기본 높이가 서로 달라서(패딩·네이티브 렌더 차이) 내용에 맡기면 칼럼마다 어긋난다.
  const b4RowGrid = { display: 'grid', gridTemplateRows: `repeat(3, ${B4_ROW_H}px)`, rowGap: 4 };
  const b4Grid2 = { ...b4RowGrid, gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 8 }; // 라벨+컨트롤
  const b4Grid1 = { ...b4RowGrid, gridTemplateColumns: 'minmax(0, 1fr)' }; // 컨트롤만
  const b4LabelStyle = { fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' };
  const b4InputStyle = { width: '100%', height: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 8, padding: '3px 8px', fontSize: 12, textAlign: 'center' };
  const b4SelectStyle = { width: '100%', height: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 8, padding: '2px 4px', fontSize: 12 };
  // 안내 문구 — 두 칼럼을 가로지른다
  const b4HintStyle = { gridColumn: '1 / -1', display: 'flex', alignItems: 'center', fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  // 면별 패턴 요약 — 면마다 배지 하나. 행 높이(24px) 안에 들어가야 하므로 배지는 17px 고정.
  const b4BadgeStrip = { display: 'flex', alignItems: 'center', gap: 3, height: '100%', overflow: 'hidden' };
  const b4Badge = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    height: 17, borderRadius: 5, fontSize: 10, lineHeight: 1, fontWeight: 700, boxSizing: 'border-box',
  };
  const b4CountBadge = { ...b4Badge, padding: '0 6px', background: '#eef0f4', color: '#6b7280' };
  const b4PatBadge = { ...b4Badge, minWidth: 17, padding: '0 4px', fontFamily: 'monospace', background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' };
  // 면별로 직접 지정한 면은 진하게 — 어느 면을 손댔는지 요약만 보고 안다
  const b4PatBadgePinned = { ...b4PatBadge, background: '#374151', color: '#fff', border: '1px solid #374151' };

  return createPortal(
    // 바깥: 화면 전체 딤(기존 미리보기 모달과 동일한 오버레이 규격) — 인쇄 시 #pv4-portal 규칙으로 초기화된다.
    // 닫기는 "딤에서 눌러서 딤에서 뗀" 경우만. click만 보면 카드 안에서 시작한 드래그
    // (입력창·미리보기 텍스트 선택)를 딤에서 놓을 때 공통 조상인 이 div로 click이 올라와 닫힌다.
    <div
      id={PV4_PORTAL_ID}
      style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999990, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={(e) => { dimPressRef.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (dimPressRef.current && e.target === e.currentTarget) close(); }}
    >
      {/* 모달 카드 — 기존 PrintPreviewModal(v2)과 동일 규격: 95vw(lg 960px) × 90vh */}
      <div
        className="pv4-root relative z-10 flex w-[95vw] flex-col overflow-hidden rounded-3xl bg-white shadow-lg lg:w-[960px]"
        style={{ height: '90vh', maxHeight: '90vh' }}
      >
        {/* 툴바 — 1행: 공통 컨트롤 / 2행(B4): 헤더 입력·마커. 카드 폭(960px)에 맞춰 행을 분리한다 */}
        <div className="pv4-no-print" style={{ flexShrink: 0, background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* 1행 — 용지·저장 상태·포함 옵션·배율·인쇄. 저장 상태 문구는 남은 폭에 맞춰 줄어든다. */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 14, flexShrink: 0 }}>인쇄 미리보기</span>

            {/* 용지 분기 */}
            <span style={{ display: 'flex', gap: 4, marginLeft: 8, flexShrink: 0 }}>
              {Object.values(PAPERS_V4).map((p) => (
                <button key={p.key} className={segBtn(paperKey === p.key)} onClick={() => setPaperKey(p.key)}>
                  {p.label}
                </button>
              ))}
            </span>
            {paperKey === 'B4' && printContext && (
              <div
                role="status"
                aria-live="polite"
                title={draftStatusText}
                className={draftStatus === 'unavailable' ? 'text-amber-700' : 'text-gray-500'}
                style={{ fontSize: 11, minWidth: 0, flexShrink: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {draftStatusText}
              </div>
            )}

            {/* 포함 옵션(A4/B4 공통) — 기본 미포함 */}
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <button className={segBtn(includeAnswers)} onClick={() => setIncludeAnswers((v) => !v)} title="빠른정답 페이지 포함 여부">
                빠른정답
              </button>
              <button className={segBtn(includeSolutions)} onClick={() => setIncludeSolutions((v) => !v)} title="풀이 페이지 포함 여부">
                풀이 포함
              </button>
            </span>

            {/* 배율 */}
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <button className={segBtn(false)} onClick={() => setZoom((z) => Math.max(0.2, +(z - 0.1).toFixed(2)))}>−</button>
              <span style={{ fontSize: 12, minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
              <button className={segBtn(false)} onClick={() => setZoom((z) => Math.min(1.2, +(z + 0.1).toFixed(2)))}>+</button>
            </span>

            <button
              onClick={handlePrint}
              disabled={printBlocked}
              title='프린터 설정: 여백 "없음" · 배율 100% · 배경 그래픽 켬. PDF가 필요하면 대상에서 "PDF로 저장" 선택'
              style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, whiteSpace: 'nowrap', background: '#4ABC85', color: '#fff', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', opacity: printBlocked ? 0.5 : 1 }}
            >
              <Printer size={16} />
              {printing ? '인쇄 준비 중…' : solutionsPending ? '풀이 불러오는 중…' : includeAnswers && answersLoading ? '정답 불러오는 중…' : '인쇄 / PDF 저장'}
            </button>
            <button onClick={close} className="pv4-close" style={{ border: '1px solid #d1d5db', borderRadius: '50%', padding: 4, flexShrink: 0, background: '#fff', cursor: 'pointer', display: 'flex' }} aria-label="닫기">
              <X size={18} />
            </button>
          </div>

          {/* 풀이 실패 안내 — 일부만 실패해도 인쇄는 막지 않고, 인쇄물이 어떻게 나오는지 그대로 말한다 */}
          {includeSolutions && !solutionsLoading && solutionsFailedCount > 0 && (
            <div role="alert" className="text-amber-700" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
              <span>
                {solutions.length === 0
                  ? '풀이를 불러오지 못했습니다. 지금 인쇄하면 풀이 면이 빠집니다.'
                  : `풀이 ${solutionsFailedCount}개를 불러오지 못했습니다. 지금 인쇄하면 그 자리에 「풀이를 불러오지 못함」이 찍힙니다.`}
              </span>
              {onRetrySolutions && (
                <button type="button" className={segBtn(false)} style={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={onRetrySolutions}>다시 시도</button>
              )}
            </div>
          )}

          {/* 2행(B4 전용) — 인쇄 헤더와 같은 순서(위·중앙·아래)로 세로 배치.
              라벨/컨트롤 2열 그리드라 세 줄의 좌·우 끝선이 정확히 일치한다. */}
          {paperKey === 'B4' && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
              {/* 헤더 3줄 */}
              <div style={{ flex: '2 1 0', minWidth: 0 }}>
                <BlockHead label="헤더" dirty={headerDirty} onReset={resetHeader} />
                <div style={b4Grid2}>
                  <span style={b4LabelStyle}>위</span>
                  <input
                    type="text"
                    value={b4Line1}
                    onChange={(e) => updateB4({ b4Line1: e.target.value })}
                    placeholder="예: 2027학년도 SKYF모의고사"
                    title="인쇄 시 헤더 맨 윗줄"
                    style={b4InputStyle}
                  />
                  <span style={b4LabelStyle}>중앙</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, height: '100%' }}>
                    <input
                      type="text"
                      value={b4Title}
                      onChange={(e) => updateB4({ b4Title: e.target.value })}
                      placeholder="수학 영역"
                      title="헤더 중앙 대제목 — 첫 장 40pt, 이후 장 22pt"
                      style={{ ...b4InputStyle, flex: '0 0 96px', fontWeight: 700, color: '#111' }}
                    />
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>(</span>
                    <input
                      type="text"
                      value={b4Subject}
                      onChange={(e) => updateB4({ b4Subject: e.target.value })}
                      placeholder="예: 확률과 통계"
                      title='"수학 영역(○○)" 괄호 표기 — 비우면 괄호 없음'
                      style={{ ...b4InputStyle, flex: 1, minWidth: 0 }}
                    />
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>)</span>
                  </span>
                  <span style={b4LabelStyle}>아래</span>
                  <input
                    type="text"
                    value={b4Line2}
                    onChange={(e) => updateB4({ b4Line2: e.target.value })}
                    placeholder="9월대비"
                    title="인쇄 시 제목 아랫줄(회색)"
                    style={{ ...b4InputStyle, color: '#6b7280' }}
                  />
                  {/* 표지 — 위/중앙 줄을 그대로 물려 쓰므로 헤더 블록 안에 둔다.
                      끄면 입력칸을 감추지 않고 잠근다: 자리가 사라지면 무엇을 껐는지 안 보인다. */}
                  <span style={b4LabelStyle}>표지</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, height: '100%' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151', whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0 }}>
                      <input type="checkbox" checked={b4Cover} onChange={(e) => updateB4({ b4Cover: e.target.checked })} />
                      앞장 넣기
                    </label>
                    <input
                      type="text"
                      value={b4CoverPhrase}
                      onChange={(e) => updateB4({ b4CoverPhrase: e.target.value })}
                      disabled={!b4Cover}
                      placeholder="필적 확인 문구"
                      title="표지 유의사항 안 회색 박스 — 필적 확인란에 적을 문구"
                      style={{ ...b4InputStyle, flex: 1, minWidth: 0, opacity: b4Cover ? 1 : 0.5 }}
                    />
                  </span>
                </div>
              </div>

              <div style={{ alignSelf: 'stretch', width: 1, background: '#e5e7eb' }} />

              {/* 유형 마커 — 지정한 문제 바로 위에 붙는다(페이지·칼럼은 배치 결과를 따라감) */}
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <BlockHead label="유형 마커" sub="(문제 위)" dirty={markersDirty} onReset={resetMarkers} />
                <div style={{ ...b4Grid2, gridTemplateRows: `repeat(4, ${B4_ROW_H}px)`, gridTemplateColumns: 'minmax(72px, 1fr) minmax(0, 1fr)' }}>
                  {markerRows.map((m) => (
                    <React.Fragment key={m.key}>
                      <input
                        type="text"
                        value={m.label}
                        onChange={(e) => m.setLabel(e.target.value)}
                        maxLength={B4_MARKER_LABEL_MAX_LENGTH}
                        aria-label={`${m.key === 'five' ? '5지선다형' : m.key === 'short' ? '단답형' : '추가'} 마커 문구`}
                        title={`문제 위에 인쇄할 마커 문구 (최대 ${B4_MARKER_LABEL_MAX_LENGTH}자)`}
                        style={b4InputStyle}
                      />
                      <select
                        value={markerNo(m.value)}
                        onChange={(e) => m.set(e.target.value)}
                        aria-label={`${m.key === 'five' ? '5지선다형' : m.key === 'short' ? '단답형' : '추가'} 마커 번호`}
                        title="이 문제 바로 위에 마커를 붙입니다"
                        style={b4SelectStyle}
                      >
                        <option value="none">없음</option>
                        {Array.from({ length: problems.length }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={String(n)} disabled={isB4MarkerNumberTaken(markerNumbers, m.numberKey, n)}>{n}번</option>
                        ))}
                      </select>
                    </React.Fragment>
                  ))}
                  <span style={b4HintStyle}>재배치돼도 지정한 문제를 따라갑니다</span>
                </div>
              </div>

              <div style={{ alignSelf: 'stretch', width: 1, background: '#e5e7eb' }} />

              {/* 면 배치 — 문서 전체의 기본 배열. 용지 위 컨트롤로 지정한 면이 항상 이것을 이긴다.
                  면마다 배지가 하나씩 붙으므로 다른 영역보다 폭을 조금 더 준다 */}
              <div style={{ flex: '1.4 1 0', minWidth: 0 }}>
                <BlockHead label="문제 배치" sub="(기본 패턴)" dirty={layoutDirty} onReset={resetLayout} />
                <div style={b4Grid1}>
                  <select
                    value={patPreset[paperKey]}
                    onChange={(e) => {
                      const key = e.target.value;
                      setPreset(paperKey, key);
                      setPatOverrides((prev) => ({ ...prev, [paperKey]: {} }));
                    }}
                    title="면마다 문항이 어떻게 놓일지의 기본값 — 용지 위 컨트롤로 면별 변경"
                    style={b4SelectStyle}
                  >
                    {presetKeysFor(paperKey).map((k) => (
                      <option key={k} value={k}>{PRESETS[k].label}</option>
                    ))}
                  </select>
                  <span style={b4BadgeStrip} title={pageBadges.map((b) => `${b.no}면 ${b.pattern}`).join(' · ')}>
                    {pageBadges.length > 0 ? (
                      <>
                        <span style={b4CountBadge}>{pageBadges.length}면</span>
                        {pageBadges.map((b) => (
                          <span
                            key={`pg-${b.no}`}
                            style={b.pinned ? b4PatBadgePinned : b4PatBadge}
                            title={`${b.no}면 · ${b.pattern}${b.pinned ? ' (직접 지정)' : ''}`}
                          >
                            {b.pattern}
                          </span>
                        ))}
                      </>
                    ) : (
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>—</span>
                    )}
                  </span>
                  <span style={b4HintStyle}>용지 위 버튼으로 면마다 변경</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 시트 영역 — 배경(용지 대비색)은 카드 안쪽으로 옮긴다(인쇄 시 .pv4-scroll 규칙이 흰색으로 초기화) */}
        <div className="pv4-scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16, background: '#4b5563' }}>
          {settling ? (
            <div style={{ color: '#fff', textAlign: 'center', paddingTop: 60, fontSize: 14 }}>실측 규격으로 배치 중…</div>
          ) : (
            <div className="pv4-zoom" style={{ zoom }}>
              <PrintSheetV4 problemPages={problemPages} answers={includeAnswers ? answers : EMPTY_SOLUTIONS} solutionPages={solutionPages} geo={geo} meta={sheetMeta} renderPageOverlay={renderPageOverlay} />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default PrintOverlayV4;
